/**
 * "Submit to Resourcing" — whoever can edit the plan sends it to Resourcing, moving its
 * Resourcing status from Not set, or Rejected, to In Review (workspace
 * feat-staffing-plan-status-workflow, 2026-10-28). The sales status does not change.
 *
 * Outline, not primary: the header's one primary action stays the sales status move
 * (Propose, …), per the "Calm first view" recipe.
 *
 * The button shows whenever the plan could go; the dialog says what still stands in the way
 * — no NOS Deal, or a rejection nobody has acknowledged — and keeps Submit disabled until
 * it is fixed. The server applies the same rule (src/lib/plan-submission.ts).
 */

import { AlertTriangle, Send } from "lucide-react";
import { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSubmitPlanToResourcing } from "@/hooks/use-resource-plans";
import { SUBMIT_NEEDS_DEAL_MESSAGE, submitToResourcingBlock } from "@/lib/plan-submission";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

export function SubmitToResourcingButton({
  plan,
  onViewRejection,
}: {
  plan: ResourcePlanDetailView;
  onViewRejection: () => void;
}) {
  const [open, setOpen] = useState(false);
  if (plan.canSubmitToResourcing !== true) return null;
  const resubmit = plan.resourcingStatus === "Returned";
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Send className="h-3.5 w-3.5" />
        {resubmit ? "Resubmit to Resourcing" : "Submit to Resourcing"}
      </Button>
      {open && (
        <SubmitToResourcingDialog
          plan={plan}
          onClose={() => setOpen(false)}
          onViewRejection={() => {
            setOpen(false);
            onViewRejection();
          }}
        />
      )}
    </>
  );
}

function SubmitToResourcingDialog({
  plan,
  onClose,
  onViewRejection,
}: {
  plan: ResourcePlanDetailView;
  onClose: () => void;
  onViewRejection: () => void;
}) {
  const submit = useSubmitPlanToResourcing(plan.id);
  const block = submitToResourcingBlock(plan);
  // RP-34: a rejection is a Blocking Concern, and leaving it needs the account side to have
  // acknowledged it. Still Open means nobody has.
  const unacknowledged =
    plan.resourcingStatus === "Returned" && (plan.concerns?.blockingOpen ?? 0) > 0;
  const blocked = block !== null || unacknowledged;

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Submit to Resourcing</DialogTitle>
          <DialogDescription>
            {`Resourcing will review "${plan.name}" and staff it. You can keep editing the plan.`}
          </DialogDescription>
        </DialogHeader>

        {block !== null && (
          <Alert>
            <AlertTriangle aria-hidden />
            <AlertTitle>
              {block === SUBMIT_NEEDS_DEAL_MESSAGE ? "No NOS Deal" : "Can't submit"}
            </AlertTitle>
            <AlertDescription>
              {block === SUBMIT_NEEDS_DEAL_MESSAGE
                ? `${block} Use Link NOS Deal at the top of the plan.`
                : block}
            </AlertDescription>
          </Alert>
        )}
        {block === null && unacknowledged && (
          <Alert>
            <AlertTriangle aria-hidden />
            <AlertTitle>Rejection not acknowledged</AlertTitle>
            <AlertDescription>
              Acknowledge the rejection on the Activity tab before submitting the plan again.
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          {block === null && unacknowledged ? (
            <Button variant="outline" onClick={onViewRejection}>
              View rejection
            </Button>
          ) : (
            <Button variant="outline" onClick={onClose} disabled={submit.isPending}>
              Cancel
            </Button>
          )}
          <Button
            disabled={blocked || submit.isPending}
            onClick={() => submit.mutate(undefined, { onSuccess: onClose })}
          >
            {submit.isPending ? "Submitting…" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
